const Policy = require('../../model/Policy');
const Archive = require('../../model/Archive');
const TableState = require('../../model/TableState');
const BackupWindow = require('../../model/BackupWindow');
const Filter = require('../../../shared/ctxFiltersPanel/ctxFilterModel');
const { permissions } = require('../../constants/permissions');

class PolicyPersistenceService {
    constructor($rootScope, $filter, $q, crudService, sharedPersistenceService, storageService, iamPersistenceService) {
        this.$rootScope = $rootScope;
        this.$filter = $filter;
        this.$q = $q;
        this._crudService = crudService;
        this.storageService = storageService;
        this.iamPersistenceService = iamPersistenceService;
        this.POLICY_REST = sharedPersistenceService.getRestEndpoints().POLICY_REST;
        this.TARGET_REST = sharedPersistenceService.getRestEndpoints().TARGET_ENDPOINT;
        this.BACKUP_WINDOW_REST = sharedPersistenceService.getRestEndpoints().BACKUP_WINDOW_REST;
        this.SNAPSHOT_REST = sharedPersistenceService.getRestEndpoints().SNAPSHOT;
        this.ARCHIVE_REST = sharedPersistenceService.getRestEndpoints().ARCHIVING_REST;
        this.SUBSCRIPTIONS_REST = sharedPersistenceService.getRestEndpoints().SUBSCRIPTIONS_REST;
        this.$rootScope.$on('logout', () => {
            this.policiesTableState = undefined;
            this.clearFilter();
            this.storageService.clearCustomPageSize('policies');
        });
        this.$rootScope.$on('force-logout', () => {
            this.policiesTableState = undefined;
            this.clearFilter();
            this.storageService.clearCustomPageSize('policies');
        });
        this.initFilter();
    }

    initFilter() {
        this.filters = [];
        this.filters.push(
            Filter.getCheckboxFilter(
                this.$filter('translate')('vm.filters.compliancy'),
                Policy.getCompliancyKey(),
                Policy.getCompliancyOptions(),
                'compliancy'
            )
        );
    }

    clearFilter() {
        if (this.policiesTableState) this.policiesTableState.page = 1;
        this.filters.forEach((filter) => {
            filter.clear();
        });
    }

    getPolicy(uuid) {
        if (!this.iamPersistenceService.hasPermissions(permissions.POLICY_LISTING)) {
            return this.$q((resolve) => {
                resolve([]);
            });
        }
        return this._crudService.get(`${this.POLICY_REST}/${encodeURI(uuid)}`);
    }

    getPolicies(firstPage, isExport) {
        if (!this.iamPersistenceService.hasPermissions(permissions.POLICY_LISTING)) {
            return this.$q((resolve) => {
                resolve([]);
            });
        }

        if (firstPage && this.policiesTableState) {
            this.policiesTableState.page = 1;
        }
        if (!this.policiesTableState) {
            this.policiesTableState = new TableState();
            this.policiesTableState.orderBy = 'name';
            // check if custom pageSize is set
            let tempPageSize = this.storageService.getCustomPageSize('policies');
            if (tempPageSize) {
                this.policiesTableState.pageSize = tempPageSize;
            }
        }

        return this._crudService
            .getPagedWithStructuredFilter(
                this.POLICY_REST,
                this.policiesTableState,
                this.filters,
                null,
                Policy.getSearchKey(),
                null,
                isExport
            )
            .then((res) => {
                let searchBy = undefined;
                if (angular.isDefined(this.policiesTableState.searchBy)) {
                    searchBy = this.policiesTableState.searchBy;
                }
                this.policiesTableState = res.data.tableState;
                this.policiesTableState.searchBy = searchBy;

                if (this.policiesTableState && this.policiesTableState.pageSize)
                    this.storageService.setCustomPageSize('policies', this.policiesTableState.pageSize);
                return res.data.entities.map(Policy.fromRaw);
            });
    }

    getAllPolicies(searchBy, isExport) {
        let tableState = new TableState();
        tableState.pageSize = 1000000;
        if (searchBy) {
            tableState.searchBy = this.policiesTableState.searchBy;
        }
        return this._crudService
            .getPagedWithStructuredFilter(this.POLICY_REST, tableState, this.filters, null, null, null, isExport)
            .then((res) => {
                return res.data.entities.map(Policy.fromRaw);
            });
    }

    setDefaultPolicy(policy, allDefault) {
        return this._crudService.patch(`${this.POLICY_REST}/setDefault/${policy.uuid}?doAssign=${allDefault}`);
    }

    resetDefaultPolicy() {
        return this._crudService.patch(`${this.POLICY_REST}/resetDefault`);
    }

    createPolicy(policy) {
        return this._crudService.post(this.POLICY_REST, policy.toRaw());
    }

    updatePolicy(policy) {
        return this._crudService.put(`${this.POLICY_REST}/${policy.uuid}`, policy);
    }

    deletePolicy(policy) {
        return this._crudService.del(this.POLICY_REST, policy.uuid);
    }

    checkForPolicy(from, item) {
        return this._crudService.get(`${from}/${item.uuid}/policies`).then((res) => res.data.entities);
    }

    getTargets(filterKey, filterValue) {
        return this._crudService.get('targets/dropDown').then((res) => {
            return res.data.entities.map((i) => ({
                label: i.label,
                value: i.value,
                kind: i.kind,
            }));
        });
    }

    getBackupWindowsDropdown() {
        return this._crudService.get(this.BACKUP_WINDOW_REST + '/dropDown').then((res) => {
            return res.data.entities;
        });
    }

    getManagerTimezone() {
        return this._crudService.get('timeZones/manager', null, null, null, null, true, false, true).then((res) => {
            return res.data.entities[0];
        });
    }

    getUTCTimezonesDropdown() {
        return this._crudService.get('timeZones/dropDown', null, null, null, null, true, false, true).then((res) => {
            return res.data.entities;
        });
    }

    getBackupWindows(firstPage) {
        if (!this.iamPersistenceService.hasPermissions(permissions.BW_LISTING)) {
            return this.$q((resolve) => {
                resolve([]);
            });
        }

        if (firstPage && this.bkWinTableState) {
            this.bkWinTableState.page = 1;
        }
        return this._crudService
            .getPaged(this.BACKUP_WINDOW_REST, this.bkWinTableState, null, null, null, 10)
            .then((res) => {
                this.bkWinTableState = res.data.tableState;
                return res.data.entities.map(BackupWindow.fromRaw);
            });
    }

    getBackupWindow(bkwName) {
        return this._crudService.getById(this.BACKUP_WINDOW_REST, bkwName).then((res) => {
            let temp = res.data.entities[0];
            if (!temp) {
                return null;
            }
            return BackupWindow.fromRaw(temp);
        });
    }

    createBackupWindow(bkw) {
        return this._crudService.post(this.BACKUP_WINDOW_REST, bkw.toRaw());
    }

    updateBackupWindow(bkw, oldBkwName) {
        return this._crudService.put(`${this.BACKUP_WINDOW_REST}/${encodeURI(bkw.uuid)}`, bkw.toRaw());
    }

    deleteBackupWindow(bkw) {
        return this._crudService.del(`${this.BACKUP_WINDOW_REST}/${encodeURI(bkw.uuid)}`);
    }

    newBackupWindow() {
        return new BackupWindow();
    }

    getMaxNumberOfSnapshots() {
        return this._crudService.get(this.SNAPSHOT_REST + '/maxSnapshotsInfo').then((res) => {
            return res.data.entities[0];
        });
    }

    getAllArchives() {
        return this._crudService.get(this.ARCHIVE_REST).then((res) => {
            return res.data.entities.map(Archive.fromRaw);
        });
    }

    getArchives(firstPage) {
        if (!this.iamPersistenceService.hasPermissions(permissions.ARCHIVES_LISTING)) {
            return this.$q((resolve) => {
                resolve([]);
            });
        }

        if (firstPage && this.archiveTableState) {
            this.archiveTableState.page = 1;
        }
        return this._crudService
            .getPaged(this.ARCHIVE_REST, this.archiveTableState, null, null, null, 10)
            .then((res) => {
                this.archiveTableState = res.data.tableState;
                return res.data.entities.map(Archive.fromRaw);
            });
    }

    getArchive(archiveId, withSpecifics) {
        return this._crudService.get(this.ARCHIVE_REST + '/' + archiveId).then((res) => {
            let temp = res.data.entities[0];
            if (!temp) {
                return null;
            }
            return withSpecifics
                ? this.getArchiveSpecifics(archiveId).then((res) => {
                      temp.archiveSpecifics = res ? res : [];
                      return Archive.fromRaw(temp);
                  })
                : Archive.fromRaw(temp);
        });
    }

    getArchiveSpecifics(id) {
        return this._crudService.getById(this.ARCHIVE_REST, id).then((res) => {
            return res.data.entities;
        });
    }

    getArchivesDropdown() {
        return this._crudService.get(this.ARCHIVE_REST + '/dropDown').then((res) => {
            return res.data.entities;
        });
    }

    newArchive() {
        return new Archive();
    }

    createArchive(archive) {
        return this._crudService.post(this.ARCHIVE_REST, archive.toRaw());
    }

    updateArchive(archive) {
        return this._crudService.put(`${this.ARCHIVE_REST}/${encodeURI(archive.uuid)}`, archive.toRaw());
    }

    deleteArchive(archive) {
        return this._crudService.del(`${this.ARCHIVE_REST}/${encodeURI(archive.uuid)}`);
    }

    setDefaultPolicy(policy, allDefault) {
        let data = {
            setToAllVirtualMachinesWithoutPolicy: allDefault,
        };
        return this._crudService.post(`${this.POLICY_REST}/${policy.uuid}/setAsDefault`, data);
    }

    resetDefaultPolicy() {
        return this._crudService.del(`${this.POLICY_REST}/unsetDefault`);
    }
}

PolicyPersistenceService.$inject = [
    '$rootScope',
    '$filter',
    '$q',
    'crudService',
    'sharedPersistenceService',
    'storageService',
    'iamPersistenceService',
];

module.exports = PolicyPersistenceService;



// WEBPACK FOOTER //
// ./src/azurebackup/views/policies/policies.persistence.service.js